/******************************************************************************
 * FILE:
 * src/modules/expenseManagement/workflow/services/workflowStepService.js
 *
 * MODULE:
 * Expense Management
 *
 * FEATURE:
 * Workflow Step
 *
 * PURPOSE:
 * Frontend API Client for Workflow Step Management
 *
 * ARCHITECTURE:
 * • Axios Client
 * • No Business Logic
 * • Returns Backend Response Data
 ******************************************************************************/

import axiosClient from "../../../../api/axiosClient";

/**
 * --------------------------------------------------------------------------
 * GET WORKFLOW STEPS
 * --------------------------------------------------------------------------
 *
 * Backend:
 * GET /api/expense-management/workflow-step/list/:workflowId
 */
async function getWorkflowSteps(
  workflowId,
  params = {}
) {

  const res = await axiosClient.get(
    `/api/expense-management/workflow-step/list/${workflowId}`,
    {
      params,
    }
  );

  return res.data.data || [];
}

/**
 * --------------------------------------------------------------------------
 * GET WORKFLOW STEP DETAILS
 * --------------------------------------------------------------------------
 *
 * Backend:
 * GET /api/expense-management/workflow-step/details/:workflowStepId
 */
async function getWorkflowStep(
  workflowStepId
) {

  const res = await axiosClient.get(
    `/api/expense-management/workflow-step/details/${workflowStepId}`
  );

  return res.data.data;
}

/**
 * --------------------------------------------------------------------------
 * CREATE WORKFLOW STEP
 * --------------------------------------------------------------------------
 *
 * Backend:
 * POST /api/expense-management/workflow-step/create
 */
async function createWorkflowStep(
  payload
) {

  const res = await axiosClient.post(
    "/api/expense-management/workflow-step/create",
    payload
  );

  return res.data.data;
}

/**
 * --------------------------------------------------------------------------
 * UPDATE WORKFLOW STEP
 * --------------------------------------------------------------------------
 *
 * Backend:
 * PUT /api/expense-management/workflow-step/update/:workflowStepId
 */
async function updateWorkflowStep(
  workflowStepId,
  payload
) {

  const res = await axiosClient.put(
    `/api/expense-management/workflow-step/update/${workflowStepId}`,
    payload
  );

  return res.data.data;
}

/**
 * --------------------------------------------------------------------------
 * DELETE WORKFLOW STEP
 * --------------------------------------------------------------------------
 *
 * Backend:
 * DELETE /api/expense-management/workflow-step/delete/:workflowStepId
 */
async function deleteWorkflowStep(
  workflowStepId
) {

  const res = await axiosClient.delete(
    `/api/expense-management/workflow-step/delete/${workflowStepId}`
  );

  return res.data;
}

/**
 * --------------------------------------------------------------------------
 * MOVE WORKFLOW STEP UP
 * --------------------------------------------------------------------------
 *
 * Backend:
 * PATCH /api/expense-management/workflow-step/move-up/:workflowStepId
 */
async function moveWorkflowStepUp(
  workflowStepId
) {

  const res = await axiosClient.patch(
    `/api/expense-management/workflow-step/move-up/${workflowStepId}`
  );

  return res.data.data;
}

/**
 * --------------------------------------------------------------------------
 * MOVE WORKFLOW STEP DOWN
 * --------------------------------------------------------------------------
 *
 * Backend:
 * PATCH /api/expense-management/workflow-step/move-down/:workflowStepId
 */
async function moveWorkflowStepDown(
  workflowStepId
) {

  const res = await axiosClient.patch(
    `/api/expense-management/workflow-step/move-down/${workflowStepId}`
  );

  return res.data.data;
}

/**
 * --------------------------------------------------------------------------
 * ACTIVATE WORKFLOW STEP
 * --------------------------------------------------------------------------
 *
 * Backend:
 * PATCH /api/expense-management/workflow-step/activate/:workflowStepId
 */
async function activateWorkflowStep(
  workflowStepId
) {

  const res = await axiosClient.patch(
    `/api/expense-management/workflow-step/activate/${workflowStepId}`
  );

  return res.data.data;
}

/**
 * --------------------------------------------------------------------------
 * DEACTIVATE WORKFLOW STEP
 * --------------------------------------------------------------------------
 *
 * Backend:
 * PATCH /api/expense-management/workflow-step/deactivate/:workflowStepId
 */
async function deactivateWorkflowStep(
  workflowStepId
) {

  const res = await axiosClient.patch(
    `/api/expense-management/workflow-step/deactivate/${workflowStepId}`
  );

  return res.data.data;
}

/**
 * --------------------------------------------------------------------------
 * EXPORT
 * --------------------------------------------------------------------------
 */

const workflowStepService = {

  getWorkflowSteps,

  getWorkflowStep,

  createWorkflowStep,

  updateWorkflowStep,

  deleteWorkflowStep,

  moveWorkflowStepUp,

  moveWorkflowStepDown,

  activateWorkflowStep,

  deactivateWorkflowStep,

};

export default workflowStepService;